import { useState } from "react";
import { api, API, auth } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileDown, Share2, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const [share, setShare] = useState(null);
  const [busyDl, setBusyDl] = useState(false);
  const [busySh, setBusySh] = useState(false);

  const download = async () => {
    setBusyDl(true);
    try {
      const r = await api.get("/reports/clinician-pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const disp = r.headers["content-disposition"] || "";
      const m = disp.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : "NutriGuide_Report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error("Could not generate PDF");
    } finally { setBusyDl(false); }
  };

  const makeLink = async () => {
    setBusySh(true);
    try {
      const r = await api.post("/reports/share-link");
      setShare(r.data);
      toast.success("Share link created (7 days)");
    } catch (e) {
      toast.error("Could not create link");
    } finally { setBusySh(false); }
  };

  const shareUrl = share ? `${API}/reports/shared/${share.token}` : "";

  const copy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied");
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto">
      <div className="mb-8 fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Clinician report</h1>
        <p className="text-slate-500 mt-2">Take a polished PDF of your health profile, tracking, and meal plan to your next appointment.</p>
      </div>

      <Card className="rounded-3xl border-emerald-100 p-6 mb-5 fade-up delay-1">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
            <FileDown className="w-6 h-6 text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-semibold text-slate-900">Download PDF</h3>
            <p className="text-sm text-slate-500 mt-1">
              Includes patient overview, conditions, medications, recent weight/glucose/BP charts,
              drug-food interactions, and the current meal plan.
            </p>
            <Button onClick={download} disabled={busyDl} className="mt-4 rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="download-pdf-btn">
              {busyDl ? "Generating…" : "Download report"}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="rounded-3xl border-sky-100 p-6 fade-up delay-2">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-sky-50 flex items-center justify-center flex-shrink-0">
            <Share2 className="w-6 h-6 text-sky-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-semibold text-slate-900">Create a share link</h3>
            <p className="text-sm text-slate-500 mt-1">
              Send your clinician a secure link to view the report. Links work without a NutriGuide account and expire after 7 days.
            </p>
            {!share && (
              <Button onClick={makeLink} disabled={busySh} variant="outline" className="mt-4 rounded-full border-sky-200 text-sky-800 hover:bg-sky-50" data-testid="create-share-btn">
                {busySh ? "Creating…" : "Create share link"}
              </Button>
            )}
            {share && (
              <div className="mt-4 space-y-3" data-testid="share-result">
                <div className="bg-sky-50 rounded-xl p-3 text-xs text-slate-700 break-all border border-sky-100 font-mono" data-testid="share-link">
                  {shareUrl}
                </div>
                <div className="flex gap-2">
                  <Button onClick={copy} variant="outline" className="rounded-full" data-testid="share-copy">
                    <Copy className="w-4 h-4 mr-2" /> Copy
                  </Button>
                  <a href={shareUrl} target="_blank" rel="noreferrer">
                    <Button variant="outline" className="rounded-full" data-testid="share-open">
                      <ExternalLink className="w-4 h-4 mr-2" /> Open
                    </Button>
                  </a>
                  <span className="text-xs text-slate-400 self-center">Expires {new Date(share.expires_at).toLocaleDateString()}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      <p className="text-xs text-slate-400 mt-6 text-center">
        Reports include only data you&apos;ve entered. They&apos;re for informational use, not a medical diagnosis.
      </p>
    </div>
  );
}
